import type { ApprovalCounts, ApprovalDetail } from '@fos/shared/contracts/approvals';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { NAV_BADGES_QUERY_KEY, type NavBadges } from '../../lib/nav-badges';
import { trpc } from '../../lib/trpc';

/** Odświeżanie stanu wykonania (Wykonywana → Wykonana / Błąd). */
export const APPROVAL_POLL_MS = 1500;

/** Akcja zatwierdzona i czeka na wykonanie albo jest wykonywana (karta odpytuje serwer, aż skończy). */
export function isInFlight(a: { status: string } | undefined | null): boolean {
  return a?.status === 'approved' || a?.status === 'executing';
}

/** Po decyzji: lista, liczniki, podgląd, plakietki menu, Dziś („Do akceptacji”) i Inbox (element „zrobiony”). */
export function useApprovalsRefresh() {
  const utils = trpc.useUtils();
  const queryClient = useQueryClient();
  return useCallback(
    () =>
      Promise.all([
        utils.approvals.invalidate(),
        utils.today.invalidate(),
        queryClient.invalidateQueries({ queryKey: [['inbox']] }),
        queryClient.invalidateQueries({ queryKey: NAV_BADGES_QUERY_KEY }),
      ]),
    [utils, queryClient],
  );
}

/** Pełny podgląd z odpytywaniem w trakcie wykonania. */
export function useApprovalDetail(id: string | null, enabled = true) {
  return trpc.approvals.get.useQuery(
    { id: id ?? '' },
    {
      enabled: enabled && Boolean(id),
      refetchInterval: (query) =>
        isInFlight(query.state.data as ApprovalDetail | undefined) ? APPROVAL_POLL_MS : false,
    },
  );
}

/** Liczniki: „Czeka · N” i plakietka menu (do decyzji). */
export function useApprovalCounts(enabled = true) {
  return trpc.approvals.counts.useQuery(undefined, { enabled, staleTime: 30_000, refetchInterval: 60_000 });
}

/**
 * Plakietka menu dla Akceptacji (DESIGN §5: „Attention” — niebieska plakietka, coś do zrobienia). Do złożenia
 * w źródle plakietek (`setNavBadgesSource`, kompozycja Fali 3) razem z Inboxem:
 *   setNavBadgesSource(async () => ({
 *     ...(await inboxNavBadges(() => trpcClient.inbox.counts.query())),
 *     ...(await approvalsNavBadges(() => trpcClient.approvals.counts.query())),
 *   }))
 */
export async function approvalsNavBadges(fetchCounts: () => Promise<ApprovalCounts>): Promise<NavBadges> {
  const counts = await fetchCounts();
  return counts.decidable > 0 ? { approvals: { kind: 'attention', count: counts.decidable } } : {};
}
